import { Card, Col, Divider, message, Modal, Popconfirm, Row, Space, Tag, Typography } from "antd";
import { CustomGridTable } from "../../../blueplanitUtils/CustomGridTable";
import { AntdButton, DatePickerField, SelectInput } from "../../../blueplanitUtils/InputField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";
import JobPosting from "./JobPosting";
import { useDispatch, useSelector } from "react-redux";
import { getJobPostingAsync } from "../../../Slices/hiringSlice";
import dayjs from "dayjs";
import { useMutation } from "@apollo/client";
import { CLOSE_JOBPOSTING, PUBLISH_JOB_POSTING } from "../../../Queries/Hiring";
import { useNavigate, useParams } from "react-router-dom";

const statusColors = {
    "Requested": "#8C8C8C",
    "In-progress": "#1677FF",
    "In review": "#FAAD14",
    "Published": "#52C41A",
    "Interview In-progress": "#722ED1",
    "Hiring In-progress": "#13C2C2",
    "Hiring completed": "#237804",
};

const JobPostingList = () => {
    const { id } = useParams();
    const navigate = useNavigate()
    const navigator = useNavigate();
    const dispatch = useDispatch();
    const jobPostings = useSelector((state) => state.hiring.jobPosting);

    const [open, setOpen] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPosting, setSelectedPosting] = useState(null);
    const [selectedZones, setSelectedZones] = useState(null);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null)
    const [applyLink, setApplyLink] = useState("");


    useEffect(() => {
        dispatch(getJobPostingAsync())
    }, [dispatch]);

    const columns = [
        {
            title: 'Designation',
            dataIndex: 'designation',
            key: 'designation',
        },
        {
            title: 'Department',
            dataIndex: 'dept',
            key: 'dept',
        },
        {
            title: 'Location',
            dataIndex: 'location',
            key: 'location',
        },
        {
            title: "Employment Type",
            dataIndex: "employmentType",
        },
        {
            title: "Publish Source",
            dataIndex: "publishedSources",
            render: (sources) => sources?.join(", ") || "—",
        },
        {
            title: "Publish Date",
            dataIndex: "publishDate",
            render: (date) =>
                date ? dayjs(parseInt(date)).format("DD/MM/YYYY") : "Not Published",
        },
        {
            title: "Status",
            dataIndex: "approvalStatus",
            key: "approvalStatus",
            render: (status) => (
                <Tag
                    color={statusColors[status]}
                    style={{ fontWeight: 500, borderRadius: 5, padding: "2px 8px" }}
                >
                    {status}
                </Tag>
            ),
        },
        {
            title: "Publish Status",
            dataIndex: "publishStatus",
            render: (status) => {
                const color =
                    status === "Active"
                        ? "green"
                        : status === "InActive"
                            ? "red"
                            : "blue";
                return <Tag color={color}>{status}</Tag>;
            },
        },
        {
            title: "Actions",
            render: (_, record) => (
                <Space>

                    <FontAwesomeIcon
                        icon={faPen}
                        onClick={() => handleDetailPage(record?.id)}
                        style={{
                            cursor: "pointer",
                            fontSize: "14px",
                            color: "#1677ff",
                        }}
                    />
                    {record.publishStatus === "Draft" && (
                        <AntdButton
                            label="Publish"
                            type="primary"
                            onClick={() => openPublishModal(record)}
                        />
                    )}

                    {record.publishStatus === "Active" && (
                        <Popconfirm
                            title="Close this job posting?"
                            onConfirm={() => closePosting(record)}
                        >
                            <AntdButton label="Close" variant="solid" color="danger" />
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    const openPublishModal = (record) => {
        setSelectedPosting({
            ...record,
            publishedSources: record.publishedSources || [],
        });
        setIsModalOpen(true);
    };

    const [PublishJobPosting] = useMutation(PUBLISH_JOB_POSTING);
    const [CloseJobPosting] = useMutation(CLOSE_JOBPOSTING);

    const handlePublish = async (publishMode) => {
        if (
            !selectedPosting?.publishedSources?.length ||
            !selectedZones?.zones ||
            !selectedDepartment?.department ||
            !selectedDate
        ) {
            message.error("Please select all fields");
            return;
        }

        try {
            const { data } = await PublishJobPosting({
                variables: {
                    requisitionId: selectedPosting.requisitionId,
                    boards: selectedPosting.publishedSources,
                    zones: selectedZones.zones,
                    department: selectedDepartment.department,
                    expiryDate: selectedDate.toISOString(),
                    publishMode // 👈 NEW (REAL / TEST)
                },
            });

            const result = data?.PublishJobPosting;

            if (result?.status !== 200) {
                message.error(result?.message || "Failed to publish job");
                return;
            }

            const applyUrl = result?.response?.applyUrl;
            setApplyLink(applyUrl);

            message.success(
                publishMode === "REAL"
                    ? "Job published successfully (Real-time)"
                    : "Job published successfully (Test mode)"
            );

            dispatch(getJobPostingAsync());
        } catch (error) {
            console.error(error);
            message.error("Failed to publish job");
        }
    };

    const closePosting = async (record) => {
        try {
            await CloseJobPosting({
                variables: {
                    requisitionId: record.requisitionId
                }
            })

            message.success("job closed successfully");
            dispatch(getJobPostingAsync());
        }
        catch (error) {
            message.error("failed to close job")
        }
    }
    function fallbackCopy(text) {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);

        textarea.focus();
        textarea.select();

        try {
            document.execCommand("copy");
            message.success("Copied");
        } catch (err) {
            message.error("Copy failed");
        }

        document.body.removeChild(textarea);
    }

    const handleDetailPage = (record) => {
        navigate(`/JobPosting/${record}`)
    }

    const confirmPublish = () => {
        let actionClicked = false; 
        Modal.confirm({
            title: "Confirm Job Publishing",
            closable: true,
            centered: true,

            content: (
                <>
                    <p>Please choose how you want to publish this job:</p>
                    <ul>
                        <li>
                            <b>Real-time Publish:</b> Job will go live on actual job boards.
                        </li>
                        <li>
                            <b>Test Publish:</b> Job will be published in testing mode only.
                        </li>
                    </ul>
                </>
            ),

            okText: "Real-time Publish",
            cancelText: "Test Publish",

            onOk() {
                actionClicked = true;
                handlePublish("REAL");
            },

            onCancel() {
                if (!actionClicked) return; // ❌ close (X) or outside click → do nothing
                handlePublish("TEST");
            },
        });
    };


    return (
        <div className="page-wrapper">
            <div className="content container-fluid">

                {(id !== undefined) ? <JobPosting /> :
                    <>
                        {!open &&
                            <>

                                <div className="row">
                                    <div className="col-md-12">
                                        <div className="table-responsive">
                                            <CustomGridTable
                                                rowKey="id"
                                                style={{ width: "100%", tableLayout: "auto" }}
                                                columns={columns}
                                                dataSource={jobPostings || []}
                                                pagination={true}
                                                loading={false}
                                                scroll={{ x: false }}
                                                size="small"
                                            />
                                        </div>

                                    </div>
                                </div>

                            </>
                        }

                        {isModalOpen && (
                            <Modal
                                open={isModalOpen}
                                onCancel={() => setIsModalOpen(false)}
                                footer={null}
                                width={700}
                                centered
                            >
                                <Card title={`Job Board Posting — ${selectedPosting?.designation}`} bordered={false} style={{ marginTop: "10px" }}>
                                    <Row gutter={16} align="bottom">
                                        <Col span={8}>
                                            <SelectInput
                                                label="Select Boards"
                                                mode="multiple"
                                                value={selectedPosting?.publishedSources}
                                                onChange={(value) =>
                                                    setSelectedPosting((prev) => ({
                                                        ...prev,
                                                        publishedSources: value
                                                    }))
                                                }
                                                style={{ width: "100%" }}
                                                options={[
                                                    { label: "LinkedIn", value: "LinkedIn" },
                                                    { label: "Indeed", value: "Indeed" },
                                                    { label: "Naukri", value: "Naukri" },
                                                    { label: "Referral", value: "Referral" },
                                                    { label: "Website", value: "Website" }
                                                ]}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <SelectInput
                                                label="Select Zones"
                                                style={{ width: "100%" }}
                                                value={selectedZones?.zones}
                                                allowClear={true}
                                                onChange={(value) =>
                                                    setSelectedZones((prev) => ({
                                                        ...prev,
                                                        zones: value
                                                    }))
                                                }
                                                options={[
                                                    { label: "United States", value: "United States" },
                                                    { label: "India", value: "India" }
                                                ]}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <SelectInput
                                                label="Select Department"
                                                style={{ width: "100%" }}
                                                value={selectedDepartment?.department}
                                                allowClear={true}
                                                onChange={(value) =>
                                                    setSelectedDepartment((prev) => ({
                                                        ...prev,
                                                        department: value
                                                    }))
                                                }
                                                options={[
                                                    { label: "Sales & Marketing", value: "Sales & Marketing" },
                                                    { label: "Insurance Operations", value: "Insurance Operations" },
                                                    { label: "HR & Admin", value: "HR & Admin" },
                                                    { label: "Information Technology", value: "Information Technology" },
                                                    { label: "Networking & Cyber Security", value: "Networking & Cyber Security" }]}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <DatePickerField
                                                label="Expiry Date"
                                                name="expiryDate"
                                                style={{ width: "100%" }}
                                                format="DD/MM/YYYY"
                                                value={selectedDate}
                                                disabledDate={(current) => {
                                                    return current && current < dayjs().startOf('day');
                                                }}
                                                onChange={setSelectedDate}
                                            />
                                        </Col>
                                        <Col span={24} style={{ textAlign: "center" }}>
                                            <AntdButton
                                                label="Publish to Boards"
                                                color="primary"
                                                variant="solid"
                                                onClick={confirmPublish}
                                            />
                                        </Col>
                                    </Row>

                                    <Divider />
                                    {applyLink && (
                                        <>
                                            <strong>Apply Link:</strong>
                                            <p style={{ wordBreak: "break-all" }}>{applyLink}</p>

                                            <AntdButton
                                                label="Copy Apply Link"
                                                type="primary"
                                                onClick={() => {
                                                    if (navigator.clipboard && window.isSecureContext) {
                                                        navigator.clipboard.writeText(applyLink)
                                                            .then(() => message.success("Copied"))
                                                            .catch(() => fallbackCopy(applyLink));
                                                    } else {
                                                        fallbackCopy(applyLink);
                                                    }
                                                }}
                                            />
                                        </>
                                    )}

                                </Card>
                            </Modal>
                        )}
                    </>
                }

            </div >
        </div >
    )
};

export default JobPostingList;